import {Component, EventEmitter, Input, Output} from '@angular/core';
import {User} from '../../../models/user';
import {Company} from '../../../models/company';

@Component({
    selector: 'app-companies-settings-team',
    templateUrl: './companies-settings-team.component.html',
    styles: [':host { display: block; }']
})
export class CompaniesSettingsTeamComponent {
    @Input() users: User[] = [];
    @Input() company: Company = new Company();
    @Input() loading = false;

    @Input() isSuperAdmin = false;

    @Output() addPerson = new EventEmitter<void>();
    @Output() editPerson = new EventEmitter<User>();
    @Output() removePerson = new EventEmitter<string>();
    @Output() loginAs = new EventEmitter<User>();

    @Input() roleName: (userGroupId?: string) => string = () => '';
    @Input() initials: (user: User) => string = () => '?';

    readonly skeletonCards = [0, 1, 2];
    expandedUserIds: Record<string, boolean> = {};

    get sortedUsers(): User[] {
        return [...this.users].sort((a, b) => Number(this.isManager(b)) - Number(this.isManager(a)));
    }

    userKey(user: User, index: number): string {
        return user.id || `user-${index}`;
    }

    isExpanded(user: User, index: number): boolean {
        return !!this.expandedUserIds[this.userKey(user, index)];
    }

    toggleExpanded(user: User, index: number): void {
        const key = this.userKey(user, index);
        this.expandedUserIds = {...this.expandedUserIds, [key]: !this.expandedUserIds[key]};
    }

    isManager(user: User): boolean {
        const role = (user.userGroupId ?? '').trim().toLowerCase();
        if (role === 'companymanager') {
            return true;
        }
        const email = (user.email ?? '').trim().toLowerCase();
        const legalEmail = (this.company.legalEmail ?? '').trim().toLowerCase();
        return !!email && !!legalEmail && email === legalEmail;
    }

    identityFront(user: User): string {
        return user.documentUrlFront
            || (this.isManager(user) ? this.company.legalDocumentFront : '')
            || '';
    }

    identityBack(user: User): string {
        return user.documentUrlBack
            || (this.isManager(user) ? this.company.legalDocumentBack : '')
            || '';
    }

    fiscalFront(user: User): string {
        return user.fiscalCodeFront
            || user.fiscalCodeDocumentUrl
            || (user as User & {fiscalCodeUrlFront?: string}).fiscalCodeUrlFront
            || (this.isManager(user) ? this.company.legalFiscalCodeFront : '')
            || '';
    }

    fiscalBack(user: User): string {
        return user.fiscalCodeBack
            || (user as User & {fiscalCodeUrlBack?: string}).fiscalCodeUrlBack
            || (this.isManager(user) ? this.company.legalFiscalCodeBack : '')
            || '';
    }

    hasIdentityDocs(user: User): boolean {
        return !!(this.identityFront(user) || this.identityBack(user) || this.fiscalFront(user) || this.fiscalBack(user));
    }

    showDocuments(user: User): boolean {
        return this.isManager(user) || this.hasIdentityDocs(user);
    }

    identityDocumentsFor(user: User): Array<{label: string; url: string}> {
        return [
            {label: "Carta d'identità — fronte", url: this.identityFront(user)},
            {label: "Carta d'identità — retro", url: this.identityBack(user)},
        ];
    }

    fiscalDocumentsFor(user: User): Array<{label: string; url: string}> {
        return [
            {label: 'Codice fiscale — fronte', url: this.fiscalFront(user)},
            {label: 'Codice fiscale — retro', url: this.fiscalBack(user)},
        ];
    }

    documentsFor(user: User): Array<{label: string; url: string}> {
        return [
            ...this.identityDocumentsFor(user),
            ...this.fiscalDocumentsFor(user),
        ];
    }

    isPdf(url: string): boolean {
        return (url || '').split('?')[0].toLowerCase().endsWith('.pdf');
    }

    fileName(url: string): string {
        if (!url) {
            return '';
        }
        try {
            return decodeURIComponent(url.split('?')[0].split('/').pop() ?? 'Documento');
        } catch {
            return 'Documento';
        }
    }

    formatDate(value?: string): string {
        if (!value) {
            return '';
        }
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return value;
        }
        return date.toLocaleString('it-IT', {dateStyle: 'short', timeStyle: 'short'});
    }

    statusLabel(status?: string): string {
        const value = (status ?? '').trim().toLowerCase();
        if (value === 'active' || value === 'approved') {
            return 'Attivo';
        }
        if (value === 'pending' || value === 'pendingotp') {
            return 'In attesa';
        }
        if (value === 'inactive' || value === 'disabled') {
            return 'Disattivato';
        }
        return status || '';
    }
}
